import { useEffect, useState } from 'react';
import { useQuery } from '@apollo/react-hooks';
import { pathOr } from 'ramda';
import { DocumentNode } from 'graphql';
import gql from 'graphql-tag';
import moment from 'moment';

import { Audience } from 'scenes/Home/scenes/MerchantStatusDetails/scenes/sections/MerchantOffers/MerchantOffer.types';
import { DropDown, Option } from 'components/FormSelect/FormSelect.helpers';

type AudienceData = {
  data: Audience[];
  loading: boolean;
};

const AUDIENCE_SUBSCRIBER = 'MERCHANT_SERVICE';

export const GET_AUDIENCE_SUBSCRIPTIONS = gql`
  query GetSubscriptions($params: GetAudienceSubscriptionsInput!) {
    getAudienceSubscriptionsForSubscriber(params: $params) {
      subscriptions {
        externalId
        name
        status
        effectiveAt
        audienceUuid
      }
    }
  }
`;

const audienceTemplate = {
  options: [
    {
      key: 'select',
      value: null,
      text: 'No Audience Attached',
    },
  ],
  label: {
    children: 'Audience',
  },
  placeholder: 'No Audience Attached',
};

const buildAudienceById = (audiences: Audience[]) => {
  const mapping = {} as any;
  audiences.forEach((audience) => {
    mapping[audience.audienceUuid] = audience;
  });
  return mapping;
};

const buildOptions = (optionsData: Audience[]) => {
  const options = [
    {
      key: 'select',
      text: 'No Audience Attached',
      value: null,
    } as Option,
  ];

  optionsData.forEach((optionData: Audience) => {
    const effectiveAt = moment(optionData.effectiveAt).format('MMM Do YYYY');
    options.push({
      key: optionData.audienceUuid,
      text: `${optionData.name} - ${effectiveAt}`,
      value: optionData.audienceUuid.toString(),
    } as Option);
  });
  return options;
};

export const useMerchantAudiences = (variables: string) => {
  const [rawData, setRawData] = useState<AudienceData[]>([]);
  const [audienceById, setAudienceById] = useState<any>({});
  const [audienceOptions, setAudienceOptions] = useState<Option[]>([]);
  const [audienceDropdown, setAudienceDropdown] = useState<DropDown>(
    audienceTemplate,
  );

  const { data: audienceData = [], loading } = useQuery<AudienceData>(
    GET_AUDIENCE_SUBSCRIPTIONS as DocumentNode,
    {
      variables: {
        params: {
          subscriber: AUDIENCE_SUBSCRIBER,
          externalIdPrefix: variables,
        },
      },
    },
  );

  useEffect(() => {
    if (loading) return;

    const data = ([] = pathOr(
      [],
      ['getAudienceSubscriptionsForSubscriber', 'subscriptions'],
      audienceData,
    ));
    const audienceOptions = buildOptions(data);
    const audienceById = buildAudienceById(data);

    setRawData(data);
    setAudienceOptions(audienceOptions);
    setAudienceById(audienceById);
  }, [audienceData, loading]);

  useEffect(() => {
    setAudienceDropdown({
      options: audienceOptions,
      label: {
        children: 'Audience',
      },
      placeholder: 'No Audience Attached',
    });
  }, [audienceOptions]);

  const filteredAudienceDropdown = (audienceIds: string[]) => ({
    options: audienceOptions.filter(
      (option) => !audienceIds.some((id) => id === option.value),
    ),
    label: {
      children: 'Audience',
    },
    placeholder: 'No Audience Attached',
  });

  return {
    rawData,
    audienceOptions,
    audienceDropdown,
    audienceById,
    filteredAudienceDropdown,
  };
};
